import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Rating,
  Grid,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createReview, ReviewRequest } from '../lib/api/reviews';

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  onReviewSubmitted: () => void;
  projectId: string;
  projectTitle: string;
  provider: {
    id: string;
    name: string;
    avatar?: string;
    company?: string;
  };
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  open,
  onClose,
  onReviewSubmitted,
  projectId,
  projectTitle,
  provider,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [ratings, setRatings] = useState({
    quality: 0,
    communication: 0,
    deadline: 0,
  });
  const [comment, setComment] = useState('');

  const handleRatingChange = (category: keyof typeof ratings, value: number) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSubmit = async () => {
    if (ratings.quality === 0 || ratings.communication === 0 || ratings.deadline === 0) {
      setError('Моля, оценете всички категории');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reviewData: ReviewRequest = {
        project_id: projectId,
        provider_id: provider.id,
        rating_quality: ratings.quality,
        rating_communication: ratings.communication,
        rating_deadline: ratings.deadline,
        comment: comment.trim() || undefined,
      };

      const { data, error } = await createReview(reviewData);

      if (error) {
        setError(error);
        return;
      }

      if (data) {
        onReviewSubmitted();
        handleClose();
      }
    } catch (err) {
      setError('Неочаквана грешка при изпращане на ревюто');
      console.error('Error submitting review:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRatings({ quality: 0, communication: 0, deadline: 0 });
    setComment('');
    setError(null);
    onClose();
  };

  const isFormValid = ratings.quality > 0 && ratings.communication > 0 && ratings.deadline > 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          color: '#ffffff',
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 2,
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Оставете ревю
        </Typography>
        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
          Проект: {projectTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Provider Info */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 4,
          p: 2,
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Avatar
            src={provider.avatar}
            sx={{ width: 50, height: 50, bgcolor: '#28A0F0' }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
              {provider.name}
            </Typography>
            {provider.company && (
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {provider.company}
              </Typography>
            )}
          </Box>
          <Chip 
            label={t('common.purchasedAccess')} 
            color="success" 
            size="small"
            sx={{ 
              background: 'rgba(76, 175, 80, 0.2)',
              color: '#4caf50',
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}
          />
        </Box>

        {/* Rating Categories */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}>
              Качество на работа
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={ratings.quality}
                onChange={(_, value) => handleRatingChange('quality', value || 0)}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#ffd700',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {ratings.quality > 0 && `${ratings.quality}/5`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}>
              Комуникация
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={ratings.communication}
                onChange={(_, value) => handleRatingChange('communication', value || 0)}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#ffd700',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {ratings.communication > 0 && `${ratings.communication}/5`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}>
              Спазване на срокове
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                value={ratings.deadline}
                onChange={(_, value) => handleRatingChange('deadline', value || 0)}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#ffd700',
                  },
                  '& .MuiRating-iconEmpty': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                {ratings.deadline > 0 && `${ratings.deadline}/5`}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Comment */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}>
            Коментар (по избор)
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('common.shareExperience')}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#ffffff',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: '#28A0F0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#28A0F0',
                },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.5)',
                opacity: 1,
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: '#a0a0a0',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Отказ
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
          variant="contained"
          sx={{
            background: isFormValid 
              ? 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)'
              : 'rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
            px: 3,
            '&:hover': {
              background: isFormValid 
                ? 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)'
                : 'rgba(255, 255, 255, 0.3)',
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Изпрати ревю'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewModal;
